import React from 'react'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'

import DialogNames from '~/constants/DialogNames'
import DocumentDialogStates from '~/constants/DocumentDialogStates'
import { fileFormat, getFileFormat } from '~/constants/extensions'
import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { ConversationFile } from '~/types'
import useDialog from '~/utils/useDialog'

import AttachmentRow from '../AttachmentRow'
import MessagePlainAttachment from './MessagePlainAttachment'

const useStyles = makeStyles(
  (theme) => ({
    image: {
      marginTop: theme.spacing(1),
      maxHeight: 300,
      objectFit: 'scale-down',
      width: '100%',
    },
  }),
  { name: 'MessageAttachments' },
)

export interface MessageAttachmentsProps {
  attachments: ConversationFile[]
  isCurrentUserMessage?: boolean
  messageId: string
}

const MessageAttachments = ({
  attachments,
  isCurrentUserMessage,
  messageId,
}: MessageAttachmentsProps) => {
  const classes = useStyles()
  const isEnableCommunicationsRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.ENABLE_COMMUNICATIONS_RIGHT_RAIL),
  )
  const [openDocumentDialog] = useDialog(DialogNames.DOCUMENT)

  const attachmentsWithFormat = attachments.map((file) => ({
    ...file,
    format: getFileFormat(file.extension),
  }))

  const plainFiles = R.reject(
    R.propEq('format', fileFormat.IMAGE),
    attachmentsWithFormat,
  )
  const imageFiles = R.filter(
    R.propEq('format', fileFormat.IMAGE),
    attachmentsWithFormat,
  )

  const onPreviewRequested = (file: ConversationFile) => {
    openDocumentDialog({
      previewOnly: true,
      document: file,
      PreviewProps: {
        hideTopButtons: true,
        view: true,
        showChangeFile: false,
      },
      step: DocumentDialogStates.PREVIEW,
      conversationId: file.conversationId,
    })
  }

  const contentJustify = isCurrentUserMessage ? 'flex-end' : 'flex-start'

  if (isEnableCommunicationsRightRailEnabled) {
    return (
      <AttachmentRow
        ariaLabelledBy={`attachment-group-for-message-${messageId}`}
        attachments={attachments}
      />
    )
  }
  return (
    <Grid container>
      {!R.isEmpty(plainFiles) && (
        <Grid container item justifyContent="space-between">
          {plainFiles.map((file) => (
            <Grid item key={file.id} xs={plainFiles.length === 1 ? 12 : 6}>
              <MessagePlainAttachment
                file={file}
                onPreviewRequested={() => onPreviewRequested(file)}
              />
            </Grid>
          ))}
        </Grid>
      )}
      {!R.isEmpty(imageFiles) && (
        <Grid container item justifyContent={contentJustify}>
          {imageFiles.map((file) => (
            <Grid item key={file.id} xs={imageFiles.length === 1 ? 12 : 6}>
              <img
                alt={file.name}
                className={classes.image}
                src={file.fileUrl}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  )
}

export default MessageAttachments
